package com.tovtalk.ui.screen.splash

import androidx.compose.animation.core.EaseInOutCubic
import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.size
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.scale
import androidx.compose.ui.unit.dp
import com.tovtalk.data.local.DiscoverCache
import com.tovtalk.data.local.TokenManager
import com.tovtalk.di.koinInject
import com.tovtalk.domain.entity.LoginMethod
import com.tovtalk.domain.entity.DomainResult
import com.tovtalk.domain.repository.AppRepository
import com.tovtalk.domain.repository.AuthManager
import com.tovtalk.domain.repository.PostRepository
import com.tovtalk.domain.repository.SettingsRepository
import com.tovtalk.domain.repository.UserRepository
import com.tovtalk.ui.theme.TovTalkTheme
import com.tovtalk.utils.config.PlatformConfig
import com.tovtalk.utils.config.isWeb
import com.tovtalk.utils.deeplink.DeepLinkRoute
import com.tovtalk.utils.logger.Logger
import kotlinx.coroutines.Deferred
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import org.jetbrains.compose.resources.painterResource
import tovtalk_kmp_v2.android_ui.generated.resources.Res
import tovtalk_kmp_v2.android_ui.generated.resources.app_icon

/**
 * On Web, Firebase Auth may still have currentUser (e.g. after refresh) while we have no backend token.
 * Try to get Firebase ID token and login to backend to restore session.
 */
private suspend fun tryRestoreSessionFromFirebase(
    authManager: AuthManager,
    userRepository: UserRepository
) {
    if (authManager.getCurrentUserId() == null) return
    when (val tokenResult = authManager.getFirebaseIdToken()) {
        is DomainResult.Success -> {
            Logger.d("SplashScreen: restoring session from Firebase (has currentUser + idToken)", "Splash")
            when (val loginResult = userRepository.login(tokenResult.data, null, LoginMethod.GOOGLE, com.tovtalk.utils.config.isHadranDevice(), null)) {
                is DomainResult.Success -> Logger.d("SplashScreen: session restored from Firebase", "Splash")
                is DomainResult.Error -> Logger.d("SplashScreen: restore from Firebase failed: ${loginResult.message}", "Splash")
                else -> {}
            }
        }
        is DomainResult.Error -> Logger.d("SplashScreen: getFirebaseIdToken failed, skip restore: ${tokenResult.message}", "Splash")
        else -> {}
    }
}

/**
 * Resolves a possibly-dynamic deep link by validating slugs against the server.
 * Returns the original route if it's not StaticPage, the same StaticPage if the slug is known,
 * or null if the slug is unknown (caller should fall back to normal flow).
 *
 * The slugs deferred is awaited only when the deep link is a StaticPage, so callers
 * can kick off the fetch in parallel with other init work and pay zero latency otherwise.
 */
private suspend fun resolveDeepLinkIfStaticPage(
    pendingDeepLink: DeepLinkRoute?,
    slugsDeferred: Deferred<DomainResult<Set<String>>>?
): DeepLinkRoute? {
    if (pendingDeepLink !is DeepLinkRoute.StaticPage) return pendingDeepLink
    val slugs = when (val result = slugsDeferred?.await()) {
        is DomainResult.Success -> result.data
        else -> emptySet()
    }
    return if (pendingDeepLink.slug in slugs) pendingDeepLink else null
}

@Composable
fun SplashScreen(
    onNavigateToOfflineLaunch: () -> Unit = {},
    onNavigateToKashrutSelection: () -> Unit,
    onNavigateToLogin: () -> Unit,
    onNavigateToHome: () -> Unit,
    onNavigateToDeepLink: (DeepLinkRoute) -> Unit = {},
    onOpenUrl: (String) -> Unit = {},
    pendingDeepLink: DeepLinkRoute? = null,
    settingsRepository: SettingsRepository = koinInject(),
    appRepository: AppRepository = koinInject(),
    authManager: AuthManager = koinInject(),
    userRepository: UserRepository = koinInject(),
    postRepository: PostRepository = koinInject()
) {
    var updateRequired by remember { mutableStateOf<com.tovtalk.domain.entity.AppConfig?>(null) }

    LaunchedEffect(Unit) {
        val lang = settingsRepository.getAppLanguage()

        val hasAnyToken = TokenManager.isLoggedIn

        // Resolution result for dynamic StaticPage deep links — populated inside coroutineScope below.
        var resolvedDeepLink: DeepLinkRoute? = pendingDeepLink
        if (hasAnyToken) {
            // === Flow B: Returning user — prefetch feed in splash ===
            Logger.d("SplashScreen: Flow B (returning user)", "Splash")
            coroutineScope {
                val translationsJob = async { settingsRepository.loadTranslations(lang) }
                val configDeferred = async { appRepository.getAppConfig() }
                val feedDeferred = async {
                    when (val result = postRepository.getDiscoverFeed(cursor = null, limit = 2)) {
                        is DomainResult.Success -> {
                            DiscoverCache.setFirstPage(result.data.posts)
                            true
                        }
                        is DomainResult.Error -> {
                            Logger.d("SplashScreen: feed prefetch failed: ${result.message}", "Splash")
                            false
                        }
                        else -> false
                    }
                }
                val slugsDeferred: Deferred<DomainResult<Set<String>>>? =
                    if (pendingDeepLink is DeepLinkRoute.StaticPage) {
                        async { appRepository.getKnownStaticPageSlugs() }
                    } else null

                translationsJob.await()
                val configResult = configDeferred.await()

                // Version check
                if (configResult is DomainResult.Success) {
                    val config = configResult.data
                    val min = PlatformConfig.getPlatformMinVersion(
                        config.minVersionAndroid, config.minVersionIos, config.minVersionWeb
                    )
                    if (min != null && PlatformConfig.getAppVersionCode() < min) {
                        updateRequired = config
                        return@coroutineScope
                    }
                }

                val feedOk = feedDeferred.await()
                if (!feedOk && !TokenManager.isLoggedIn) {
                    Logger.d("SplashScreen: feed failed, no valid token -> login", "Splash")
                }

                // Resolve dynamic StaticPage deep links against server doc-slugs
                resolvedDeepLink = resolveDeepLinkIfStaticPage(pendingDeepLink, slugsDeferred)
            }
            if (updateRequired != null) return@LaunchedEffect

            // Web session restore
            if (isWeb() && TokenManager.accessToken == null) {
                tryRestoreSessionFromFirebase(authManager, userRepository)
            }

            val isLoggedIn = TokenManager.isLoggedIn
            val kashrutSelected = settingsRepository.isKashrutSelected()
            val isLegalPageDeepLink = resolvedDeepLink is DeepLinkRoute.Privacy ||
                resolvedDeepLink is DeepLinkRoute.AccountDeletion ||
                resolvedDeepLink is DeepLinkRoute.Terms ||
                resolvedDeepLink is DeepLinkRoute.About ||
                resolvedDeepLink is DeepLinkRoute.StaticPage
            val hasTargetedDeepLink = resolvedDeepLink is DeepLinkRoute.Search ||
                resolvedDeepLink is DeepLinkRoute.User ||
                resolvedDeepLink is DeepLinkRoute.Post ||
                resolvedDeepLink is DeepLinkRoute.Video ||
                resolvedDeepLink is DeepLinkRoute.Chat ||
                resolvedDeepLink is DeepLinkRoute.Privacy ||
                resolvedDeepLink is DeepLinkRoute.AccountDeletion ||
                resolvedDeepLink is DeepLinkRoute.Terms ||
                resolvedDeepLink is DeepLinkRoute.About ||
                resolvedDeepLink is DeepLinkRoute.StaticPage
            Logger.d("SplashScreen isLoggedIn=$isLoggedIn kashrutSelected=$kashrutSelected hasTargetedDeepLink=$hasTargetedDeepLink", "Splash")
            when {
                isLegalPageDeepLink -> {
                    Logger.d("SplashScreen -> onNavigateToDeepLink() (דף מידע ציבורי – בלי שפה/התחברות)", "Splash")
                    onNavigateToDeepLink(resolvedDeepLink!!)
                }
                !isLoggedIn -> onNavigateToLogin()
                !kashrutSelected -> onNavigateToKashrutSelection()
                hasTargetedDeepLink -> onNavigateToDeepLink(resolvedDeepLink!!)
                else -> onNavigateToHome()
            }
        } else {
            // === Flow A: New user — create guest token, go to onboarding ===
            Logger.d("SplashScreen: Flow A (new user)", "Splash")
            coroutineScope {
                val translationsJob = async { settingsRepository.loadTranslations(lang) }
                val configDeferred = async { appRepository.getAppConfig() }
                val guestDeferred = async { userRepository.continueAsGuest(null, null) }
                val slugsDeferred: Deferred<DomainResult<Set<String>>>? =
                    if (pendingDeepLink is DeepLinkRoute.StaticPage) {
                        async { appRepository.getKnownStaticPageSlugs() }
                    } else null

                translationsJob.await()
                val configResult = configDeferred.await()

                // Version check
                if (configResult is DomainResult.Success) {
                    val config = configResult.data
                    val min = PlatformConfig.getPlatformMinVersion(
                        config.minVersionAndroid, config.minVersionIos, config.minVersionWeb
                    )
                    if (min != null && PlatformConfig.getAppVersionCode() < min) {
                        updateRequired = config
                        return@coroutineScope
                    }
                }

                val guestResult = guestDeferred.await()
                when (guestResult) {
                    is DomainResult.Success -> Logger.d("SplashScreen: guest token created", "Splash")
                    is DomainResult.Error -> Logger.e("SplashScreen: guest token failed: ${guestResult.message}", "Splash")
                    else -> {}
                }

                // Resolve dynamic StaticPage deep links against server doc-slugs
                resolvedDeepLink = resolveDeepLinkIfStaticPage(pendingDeepLink, slugsDeferred)
            }
            if (updateRequired != null) return@LaunchedEffect

            // Legal deep links don't need onboarding
            val isLegalPageDeepLink = resolvedDeepLink is DeepLinkRoute.Privacy ||
                resolvedDeepLink is DeepLinkRoute.AccountDeletion ||
                resolvedDeepLink is DeepLinkRoute.Terms ||
                resolvedDeepLink is DeepLinkRoute.About ||
                resolvedDeepLink is DeepLinkRoute.StaticPage
            if (isLegalPageDeepLink) {
                onNavigateToDeepLink(resolvedDeepLink!!)
            } else {
                onNavigateToKashrutSelection()
            }
        }
    }

    if (updateRequired != null) {
        UpdateRequiredScreen(
            storeUrlAndroid = updateRequired!!.storeUrlAndroid,
            storeUrlIos = updateRequired!!.storeUrlIos,
            webUpdateInstructions = updateRequired!!.webUpdateInstructions,
            onOpenUrl = onOpenUrl
        )
        return
    }

    val infiniteTransition = rememberInfiniteTransition(label = "splash")
    val scale by infiniteTransition.animateFloat(
        initialValue = 0.85f,
        targetValue = 1.05f,
        animationSpec = infiniteRepeatable(
            animation = tween(durationMillis = 900, easing = EaseInOutCubic),
            repeatMode = RepeatMode.Reverse
        ),
        label = "scale"
    )
    val alpha by infiniteTransition.animateFloat(
        initialValue = 0.7f,
        targetValue = 1.0f,
        animationSpec = infiniteRepeatable(
            animation = tween(durationMillis = 900, easing = EaseInOutCubic),
            repeatMode = RepeatMode.Reverse
        ),
        label = "alpha"
    )

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(TovTalkTheme.colors.brand),
        contentAlignment = Alignment.Center
    ) {
        Image(
            painter = painterResource(Res.drawable.app_icon),
            contentDescription = null,
            modifier = Modifier
                .size(120.dp)
                .scale(scale)
                .alpha(alpha)
        )
    }
}
